import * as React from "react";
// import Fuse from "fuse.js";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FadeIn,
  Input,
  Link,
  // TagLink,
  IconSearch,
  Text,
  ScreenReaderText,
  Hr,
  Label,
  Button,
  EntityBadge,
} from "atoms";
import { useDebouncer } from "utils/hooks";
import { Thumbnail } from "features/images";
// import {
//   artistAttributes,
//   artistMediums,
//   founderAttributes,
//   missionOrCauses,
// } from "features/reviewable/data";
import { searchInputHeight } from "features/layouts/components/SiteHeader";
import type { Artist, Project } from "features/reviewable/types";
import { useIncrementalSearch } from "../api/useSearch";

// type Tag = {
//   tag: string;
//   type: React.ComponentProps<typeof TagLink>["variant"];
// };

// const tags = [
//   ...founderAttributes.map((tag) => ({ tag, type: "founderAttributes" })),
//   ...missionOrCauses.map((tag) => ({ tag, type: "missionOrCause" })),
//   ...artistAttributes.map((tag) => ({ tag, type: "founderAttributes" })),
//   ...artistMediums.map((tag) => ({ tag, type: "medium" })),
// ] as Tag[];

// const fuse = new Fuse(tags, {
//   includeMatches: true,
//   threshold: 0.7,
//   keys: ["tag"],
// });

function GlobalSearch() {
  // Search Form
  const [value, setValue] = React.useState("");
  const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setValue(event.target.value);
  };

  const [isFocused, setIsFocused] = React.useState(false);
  const onFocus = () => setIsFocused(true);

  const onBlur: React.FocusEventHandler = (event) => {
    // Keep modal up when swapping focus between children, i.e. search input and results
    if (!event.currentTarget.contains(event.relatedTarget)) {
      // NOTE: Safari (including all iOS browsers) doesn't populate `event.relatedTarget` when
      // newly focused item doesn't have explicit tabIndex, so `currentTarget.contains(null)`
      // fails even when clicking a child link. The following state change interrupts the intended
      // navigation attempt, so if we schedule the state change later in the event loop, react-
      // router can do its thing first and actually follow the clicked link, closing the modal
      // if they're still on this page after the timeout.
      setTimeout(() => {
        setIsFocused(false);
      }, 100);
    }
  };

  // Debounce the input value so we only query once user finishes typing
  const query = useDebouncer(value, 300);

  // Run query and manage state
  const { isFetching, isIdle, data } = useIncrementalSearch(query);
  const showResults = isFocused && !isIdle;

  // Memoized tag search based on search query, limited to 8 matches
  // const matchingTags = React.useMemo(
  //   () => fuse.search(query).slice(0, 8),
  //   [query]
  // );

  const location = useLocation();
  const navigate = useNavigate();

  // Reset on navigation
  React.useEffect(() => {
    setValue("");
    setIsFocused(false);
  }, [location]);

  // Hide search bar on Explore page
  const hideSearch = location.pathname.startsWith("/search");

  const searchUrl = `/search?name=${value}`;
  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (value) {
      navigate(searchUrl);
    }
  };

  return (
    <Wrapper
      onBlur={onBlur}
      isFocused={isFocused}
      hideSearch={hideSearch}
      className="GlobalSearch"
    >
      <SearchForm
        onSubmit={onSubmit}
        isFocused={isFocused}
        showResults={showResults}
      >
        <Label>
          <ScreenReaderText>Search</ScreenReaderText>

          <SearchInput
            name="search"
            placeholder="Search..."
            value={value}
            onChange={onChange}
            onFocus={onFocus}
          />
        </Label>

        <SearchButton
          type="submit"
          isFocused={isFocused}
          aria-label="Submit search"
        >
          <SearchIcon aria-hidden="true" accent="accent1" />
        </SearchButton>
      </SearchForm>

      {showResults && (
        <ResultsModal>
          {data?.length === 0 && (
            <SearchResultMessage>
              <em>No projects match your search.</em>
            </SearchResultMessage>
          )}

          <SearchResults isFetching={isFetching || value !== query}>
            {data?.map((result, index) => (
              <React.Fragment key={result.id}>
                <SearchResult delay={index * 50} result={result} />
                <SHr />
              </React.Fragment>
            ))}
          </SearchResults>

          <Explore>
            <Text>Can&apos;t find what you&apos;re looking&nbsp;for?</Text>
            <Link to="/search" variant="primary" arrow="r" size="sm">
              Search All Creators
            </Link>
          </Explore>
        </ResultsModal>
      )}
    </Wrapper>
  );
}

interface WithFocus {
  isFocused: boolean;
  showResults?: boolean;
  hideSearch?: boolean;
}

// Time for initial animations/tranisitions to expand the search bar.
// Results modal animations will be delayed until this completes.
const ANIMATION_TIME = "0.25s";

const Wrapper = styled.div<WithFocus>(({ theme, hideSearch, isFocused }) => ({
  display: hideSearch ? "none" : "block",
  flex: "1",
  paddingBottom: 10,
  position: "relative",
  zIndex: 100,
  "@media (prefers-reduced-motion: no-preference)": {
    transitionDuration: ANIMATION_TIME,
    transitionTimingFunction: "ease",
    transitionProperty: "left",
  },
  [theme.breakpoints.desktop]: {
    alignItems: "center",
    display: hideSearch ? "none" : "flex",
    justifyContent: "flex-end",
    padding: 0,
  },
}));

const SearchForm = styled.form<WithFocus>(
  ({ isFocused, showResults, theme }) => ({
    margin: 0,
    position: "relative",
    flex: 1,
    zIndex: 1,
    input: {
      paddingRight: "4rem",
      color: theme.colors.fg,
      "&:placeholder": {
        color: isFocused ? "rgba(0, 0, 0, .8)" : theme.colors.fg,
        opacity: 1,
      },
      "&:focus-within": {
        outline: 0,
      },
      ...(isFocused && {
        cursor: "pointer",
      }),
    },
  })
);

const SearchInput = styled(Input)(({ theme }) => ({
  borderRadius: theme.borderRadius.round,
  border: `2px solid ${theme.colors.fg}`,
  position: "relative",
  margin: 0,
  display: "flex",
  alignItems: "center",
  lineHeight: 1.6,
  fontSize: 16,
  maxHeight: searchInputHeight - 10,
  "&::placeholder": {
    color: theme.colors.fg80,
    opacity: 1,
  },
  "@media (prefers-reduced-motion: no-preference)": {
    transitionDuration: ANIMATION_TIME,
    transitionTimingFunction: "ease",
    transitionProperty: "left",
  },
  [theme.breakpoints.desktop]: {
    height: 36,
    minHeight: 36,
    margin: 0,
    lineHeight: 1,
    paddingTop: 2,
    paddingBottom: 0,
  },
}));

const SearchButton = styled(Button, {
  shouldForwardProp: (propName) => propName !== "isFocused",
})<WithFocus>(({ isFocused, theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  justifyContent: "center",
  fontSize: 18,
  padding: "0 .66rem",
  position: "absolute",
  bottom: 4,
  right: 4,
  top: 4,
  zIndex: 2,
  "form &": {
    minHeight: 0,
  },
  ...(!isFocused && {
    appearance: "none",
    backgroundColor: "rgb(0 0 0 / 0)",
    border: 0,
    cursor: "pointer",
    pointerEvents: "none",
  }),
  "svg path": {
    fill: isFocused ? theme.colors.bg : theme.colors.fg,
  },
  "&:hover svg path, &:focus svg path": {
    fill: isFocused ? theme.colors.fg : theme.colors.bg,
  },
}));
SearchButton.defaultProps = {
  variant: "primary",
};

const SearchIcon = styled(IconSearch)({
  height: "auto",
  pointerEvents: "none",
});

const ResultsModal = styled(FadeIn)(({ theme }) => ({
  animationDelay: "0s",
  backgroundColor: theme.colors.bg,
  border: `2px solid ${theme.colors.fg}`,
  borderRadius: theme.borderRadius.sm,
  maxHeight: "max(88vh, 100px)",
  overflowY: "auto",
  padding: "2rem 1rem",
  marginBottom: theme.spacing.gutter,
  position: "absolute",
  left: 0,
  right: 0,
  top: "100%",
}));

const SearchResultMessage = styled(FadeIn)({
  fontSize: "1rem",
  margin: 0,
}).withComponent("p");

const SearchResults = styled.ul<{ isFetching: boolean }>(({ isFetching }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  listStyleType: "none",
  margin: 0,
  opacity: isFetching ? 0.6 : 1,
  padding: 0,
  "@media (prefers-reduced-motion: no-preference)": {
    transition: "min-height ease 0.4s",
  },
}));

interface SearchResultProps {
  result: Project | Artist;
  delay: number;
}

function SearchResult({ result, delay }: SearchResultProps) {
  const { id, name, heroImage, type } = result;

  return (
    <ListItem delay={delay}>
      <SThumbnail src={heroImage} alt="" entityType={type} />

      <SLink to={`/${type.toLowerCase()}s/${id}`}>
        <Title size="xs">
          {name}
          <br />
        </Title>

        <EntityBadge entityType={type} />
      </SLink>
    </ListItem>
  );
}

const SHr = styled(Hr)(({ theme }) => ({
  backgroundColor: theme.colors.fg,
  opacity: ".15",
  margin: "2px 0",
  height: 1,
}));

const ListItem = styled(FadeIn)({
  listStyleType: "none",
  display: "flex",
  width: "max-content",
  alignItems: "center",
  gap: 10,
  padding: 0,
  margin: 0,
  position: "relative",
}).withComponent("li");

const SThumbnail = styled(Thumbnail)({
  width: 40,
});

const SLink = styled(Link)({
  textDecoration: "none",
  display: "inline-block",
  lineHeight: "1.3",
  "&::after": {
    content: "''",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
});

const Title = styled(Text)(({ theme }) => ({
  fontWeight: "bold",
  margin: 0,
  small: {
    fontWeight: "normal",
    letterSpacing: theme.spacing.letterSpacing,
    margin: 0,
  },
}));

const Explore = styled.div({
  marginTop: "2rem",
  textAlign: "center",
  p: {
    textTransform: "unset",
    marginBottom: 5,
  },
});

export { GlobalSearch };
